/**
 * TextureLoader - Centralized texture loading system
 *
 * Eliminates duplication between GameScene and EditorScene by providing
 * a single source of truth for all texture loading.
 */

import Phaser from "phaser";
import { createTileInstance } from "./registry";
import { TILE } from "../constants/gameConstants";

import { TileIndex } from "../types";

/**
 * Centralized texture loading system
 */
export class TextureLoader {
  private static loadedTextures = new Set<string>();

  /**
   * Load all PNG textures (called in preload())
   */
  static loadImages(scene: Phaser.Scene): void {
    const allTileTypes = Object.values(TILE).filter(
      (val) => typeof val === "number"
    ) as TileIndex[];

    for (const tileType of allTileTypes) {
      const instance = createTileInstance(tileType, 0, 0);
      if (!instance) continue;

      const descriptor = instance.textureDescriptor();
      if (descriptor.type === "png") {
        const textureKey = instance.textureKey();

        // Avoid duplicate loads
        if (!this.loadedTextures.has(textureKey)) {
          scene.load.image(textureKey, descriptor.path);
          this.loadedTextures.add(textureKey);
        }
      } else if (descriptor.type === "procedural") {
        const textureKey = instance.textureKey();

        // Only generate if not already exists
        if (!scene.textures.exists(textureKey)) {
          descriptor.generator(scene, textureKey);
        }
      }
    }

    // Also load UI images
    scene.load.image("start-level-bg", "images/start-level.png");
    scene.load.image("level-complete-bg", "images/level-complete.png");
  }

  /**
   * Get the texture key for a given tile type
   * Public accessor for when you need to look up a texture key
   */
  static getKeyForTileType(tileType: TileIndex): string {
    const instance = createTileInstance(tileType, 0, 0);
    return instance.textureKey();
  }
}
